import { createAsyncThunk } from '@reduxjs/toolkit';
import axios, { AxiosError } from 'axios';

import { AuthError } from '@auspost/auth0-uiim-types';

import { apiEndpoint, APP_REQUEST_CONFIGS, extLnkDeliveryPref } from 'config/track-config';
import { DeliveryPreference } from 'models/DeliveryPreference';
import { Notification, NotificationStatus, NotificationType } from 'models/Notification';
import { AppAsyncThunkOpts } from 'models/Store';
import { clearNotification, createNotification, deleteNotification } from 'store/notification';
import { AppDispatch } from 'store/store';
import axiosInstance from 'utils/axios.utils';

import { errorContent } from '../../App/common/Notification/Notification.config';
import { getKnownTokenQueryFromUrl } from '../../utils/window.utils';
import { deleteSession } from '../customer';

const putDeliveryPreferenceMessages = {
  pending: 'Setting your delivery preference...',
  success: `Done! <a href="${extLnkDeliveryPref}">Your preference</a> has been set for future eligible deliveries.`,
  fail:
    'There was an issue saving your delivery preference.' +
    ` Please try to save it <a href="${extLnkDeliveryPref}">from here.</a>`,
};

const PENDING_NOTIFICATION: Notification = {
  type: NotificationType.ALERT,
  status: NotificationStatus.INFO,
  message: putDeliveryPreferenceMessages.pending,
  canClose: false,
};

export const getDeliveryPreferences = createAsyncThunk<DeliveryPreference | null, undefined, AppAsyncThunkOpts>(
  'delivery-preferences/v2/getDeliveryPreferences',
  async (_, thunkAPI) => {
    try {
      const response = await apiGetDeliveryPreferences();
      const deliveryPreferences = response.data?.safedrop || null;
      return deliveryPreferences;
    } catch (error) {
      const authError = error as AuthError;
      if (authError.isReauthRequiredError && authError.isReauthRequiredError()) {
        handleReauthError(thunkAPI.dispatch);
      }
      if (axios.isAxiosError(error)) {
        const err = error as AxiosError;
        if (!err.isAxiosError) throw error;
        return thunkAPI.rejectWithValue({
          status: err.response?.status,
          data: err.response?.data,
          isAxiosError: err.isAxiosError,
        });
      }
    }
  }
);

export const putDeliveryPreferences = createAsyncThunk<string | undefined, string, AppAsyncThunkOpts>(
  'delivery-preferences/v2/putDeliveryPreferences',
  async (location, thunkAPI) => {
    const { dispatch, rejectWithValue } = thunkAPI;
    try {
      dispatch(createNotification(PENDING_NOTIFICATION));
      const response = await apiPutDeliveryPreferences(location);
      const { status } = response;
      if (status !== 200) return;
      handlePutDeliveryPreferenceNotification(dispatch, status);
      return;
    } catch (error) {
      const authError = error as AuthError;
      if (authError.isReauthRequiredError && authError.isReauthRequiredError()) {
        handleReauthError(dispatch);
      }
      if (axios.isAxiosError(error)) {
        const axiosErr = error as AxiosError;
        const status = axiosErr.response?.status;
        if (status) handlePutDeliveryPreferenceNotification(dispatch, status);
        return rejectWithValue({
          status: axiosErr.response?.status,
          data: axiosErr.response?.data,
          isAxiosError: axiosErr.isAxiosError,
        });
      }

      throw error;
    }
  }
);

const handleReauthError = (dispatch: AppDispatch) => {
  if (getKnownTokenQueryFromUrl()) return dispatch(deleteSession()); // Don't show session expiry notification if known
  dispatch(clearNotification());
  dispatch(
    createNotification({
      type: NotificationType.ALERT,
      status: NotificationStatus.INFO,
      message: errorContent.expiredSession,
    })
  );
  return dispatch(deleteSession());
};

function handlePutDeliveryPreferenceNotification(dispatch: AppDispatch, statusCode: number) {
  dispatch(deleteNotification(PENDING_NOTIFICATION));
  const deliveryPreferenceNotification: Notification = {
    type: NotificationType.ALERT,
    status: NotificationStatus.INFO,
    message: putDeliveryPreferenceMessages.fail,
  };
  if (statusCode === 200) {
    deliveryPreferenceNotification.status = NotificationStatus.SUCCESS;
    deliveryPreferenceNotification.message = putDeliveryPreferenceMessages.success;
  }
  dispatch(createNotification(deliveryPreferenceNotification));
}

/**
 * Makes an API call to get delivery preferences
 */
const apiGetDeliveryPreferences = () => {
  const response = axiosInstance.get(apiEndpoint.deliveryPreferencesV2, APP_REQUEST_CONFIGS.generic);
  return response;
};

const apiPutDeliveryPreferences = (safedropLocation: string) => {
  const response = axiosInstance.put(
    apiEndpoint.deliveryPreferencesV2,
    {
      safedrop: {
        location: safedropLocation,
      },
    },
    APP_REQUEST_CONFIGS.generic
  );
  return response;
};
